/**
 * Training Plan Type Definitions
 *
 * Per-week training plans, one item per (season, level, week). Each week has
 * week-level fields (training phase, skill emphasis) plus a list of daily
 * sessions. Stored in `training_plans_db` with `season` as partition key and a
 * composite `{level}#{week:03d}` sort key. A per-season config item holds the
 * season start/end dates.
 *
 * Displayed on the Dashboard and Training page (/skills) per group (a group's
 * `group_skill_level` selects which level's plan a coach sees). Editable by
 * admins. Session dates are DERIVED from the season start date + weekday, not
 * stored.
 */

/** Training plan skill levels — mirror the skills assessment levels. */
export const TRAINING_PLAN_LEVELS = ['Beginner', 'Intermediate', 'Advanced'] as const;

export type TrainingPlanLevel = (typeof TRAINING_PLAN_LEVELS)[number];

/** Days of the week, Monday-first, as stored/rendered for sessions. */
export const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

/** Zero-based offset from Monday for each day, used to derive session dates. */
export const DAY_OFFSET: Record<DayOfWeek, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

/**
 * A single daily training session within a week.
 */
export interface TrainingPlanSession {
  day_of_week: DayOfWeek;
  periodization_focus: string; // e.g. "Aerobic Endurance"
  session_type: string;        // e.g. "Climbing intervals, RPE 3-4"
  target_rpe: string;          // e.g. "RPE 3-4"
  prescribed_workout: string;
  coaches_note: string;
}

/**
 * A single weekly training plan for one skill level, containing daily sessions.
 */
export interface TrainingPlan {
  season: string;
  level: TrainingPlanLevel;
  week: number;
  training_phase: string;   // e.g. "Base 1"
  skill_emphasis: string;   // week-level, e.g. "Braking mechanics & basic modulation"
  sessions: TrainingPlanSession[];
  created_at?: string;
  updated_at?: string;
}

/**
 * Response from GET /training-plans — all plans for a season plus season
 * scheduling info. `current_week` is computed server-side from the season
 * start date (week 1 is the partial week containing the start date; week 2
 * begins the first Monday after the start date), clamped to [1, total_weeks].
 */
export interface TrainingPlansResponse {
  season: string;
  start_date: string;   // ISO date (YYYY-MM-DD)
  end_date: string;     // ISO date (YYYY-MM-DD)
  current_week: number;
  total_weeks: number;
  plans: TrainingPlan[];
}

/**
 * DTO for setting a season's start/end dates (admin only).
 */
export interface TrainingSeasonDatesDto {
  season?: string;
  start_date: string;   // ISO date (YYYY-MM-DD)
  end_date: string;     // ISO date (YYYY-MM-DD)
}

/**
 * DTO for creating or updating a training plan week.
 * `season` is optional; the backend defaults to the current season (UTC year).
 */
export interface UpsertTrainingPlanDto {
  season?: string;
  level: TrainingPlanLevel;
  week: number;
  training_phase: string;
  skill_emphasis: string;
  sessions: TrainingPlanSession[];
}

/**
 * DTO for bulk-importing a level's plans from CSV text (admin only).
 * `dry_run: true` validates + previews without writing.
 */
export interface ImportTrainingPlansDto {
  season?: string;
  level: TrainingPlanLevel;
  csv: string;
  dry_run?: boolean;
}

/** One week in a dry-run import preview. */
export interface TrainingPlanImportPreviewWeek {
  week: number;
  training_phase: string;
  skill_emphasis: string;
  session_count: number;
  /** 'replace' if a record already exists for (level, week); otherwise 'create'. */
  action: 'create' | 'replace';
  sessions: TrainingPlanSession[];
}

/** Response from a dry-run import (POST /training-plans-import with dry_run=true). */
export interface TrainingPlanImportPreview {
  dry_run: true;
  season: string;
  level: TrainingPlanLevel;
  weeks: TrainingPlanImportPreviewWeek[];
  totals: {
    weeks: number;
    sessions: number;
    weeks_new: number;
    weeks_replaced: number;
    /** Rows skipped because they didn't fit the template (titles, junk rows). */
    rows_ignored: number;
  };
}

/** Response from a confirmed (written) import. */
export interface TrainingPlanImportResult {
  season: string;
  level: TrainingPlanLevel;
  weeks_imported: number;
  sessions_imported: number;
  weeks_replaced: number;
  rows_ignored: number;
}

/**
 * DTO for deleting a training plan.
 */
export interface DeleteTrainingPlanDto {
  season?: string;
  level: TrainingPlanLevel;
  week: number;
}

/** Ordered list of the per-session text fields for consistent rendering. */
export const TRAINING_SESSION_TEXT_FIELDS: ReadonlyArray<{
  key: keyof Pick<
    TrainingPlanSession,
    'periodization_focus' | 'session_type' | 'target_rpe' | 'prescribed_workout' | 'coaches_note'
  >;
  label: string;
}> = [
  { key: 'periodization_focus', label: 'Periodization Focus' },
  { key: 'session_type', label: 'Session Type' },
  { key: 'target_rpe', label: 'Target RPE' },
  { key: 'prescribed_workout', label: 'Prescribed Workout' },
  { key: 'coaches_note', label: 'Coaches Note' },
] as const;

/** An empty session used when adding a new session row in the editor. */
export function emptySession(day: DayOfWeek = 'Mon'): TrainingPlanSession {
  return {
    day_of_week: day,
    periodization_focus: '',
    session_type: '',
    target_rpe: '',
    prescribed_workout: '',
    coaches_note: '',
  };
}

/**
 * Tailwind classes for the level toggle buttons — reuses the Beginner/Intermediate/
 * Advanced color scheme from the skills assessment UI (orange/blue/green).
 */
export function getTrainingPlanLevelButtonClasses(
  level: TrainingPlanLevel,
  isActive: boolean,
): string {
  if (!isActive) return 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50';
  switch (level) {
    case 'Beginner':
      return 'bg-orange-500 text-white border-orange-500';
    case 'Intermediate':
      return 'bg-blue-500 text-white border-blue-500';
    case 'Advanced':
      return 'bg-green-500 text-white border-green-500';
  }
}

/**
 * Derive the calendar date of a given season week's Monday.
 *
 * Mirrors the backend: week 1's Monday is the Monday of the week CONTAINING the
 * season start date; week 2's Monday is the first Monday strictly after the
 * start date; each subsequent week adds 7 days.
 */
export function weekMondayDate(startDate: string, week: number): Date | null {
  const start = parseISODate(startDate);
  if (!start) return null;
  // JS getDay(): 0=Sun..6=Sat. Convert to Monday-first index 0=Mon..6=Sun.
  const mondayFirstIdx = (start.getDay() + 6) % 7;
  const week1Monday = new Date(start);
  week1Monday.setDate(start.getDate() - mondayFirstIdx);
  const result = new Date(week1Monday);
  result.setDate(week1Monday.getDate() + (week - 1) * 7);
  return result;
}

/**
 * Derive the calendar date of a specific session within a week, from the season
 * start date, the week number, and the session's weekday.
 */
export function sessionDate(startDate: string, week: number, day: DayOfWeek): Date | null {
  const monday = weekMondayDate(startDate, week);
  if (!monday) return null;
  const result = new Date(monday);
  result.setDate(monday.getDate() + DAY_OFFSET[day]);
  return result;
}

/** Parse a YYYY-MM-DD string into a local Date (no timezone shift). */
export function parseISODate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

/** Format a Date as e.g. "Monday, July 6, 2026". */
export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
